import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import { onAuthStateChanged, onIdTokenChanged, User } from 'firebase/auth';
import { doc, onSnapshot, DocumentData } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, firestore, functions } from '../firebase'; // Import db
import { syncEmailVerificationStatus } from '../services/emailVerification';

// Define a type for our custom user data from Firestore
export interface UserProfile extends DocumentData {
  // Changed 'serwisant' to 'serviceAdmin' to match database
  role: 'client' | 'serviceAdmin' | 'admin';
  status: 'pending' | 'pending_verification' | 'active' | 'suspended' | 'rejected'; // Add status field
  // Add other profile fields as needed, e.g., displayName, email
  displayName?: string;
  email?: string;
}

export interface Claims {
  [key: string]: any; // Generic claims structure
  isAdmin?: boolean;
  isServiceAdmin?: boolean;
  isServiceProvider?: boolean;
  // Add other expected custom claims
}

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  claims: Claims | null;
  claimsLoading: boolean;
  adminAccessResolving: boolean;
  refreshClaims: (force?: boolean) => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

const normalizeProfileRole = (role: unknown): UserProfile['role'] | null => {
  const normalizedRoleValue = String(role || '')
    .trim()
    .toLowerCase();
  if (normalizedRoleValue === 'client') {
    return 'client';
  }
  if (normalizedRoleValue === 'serviceadmin' || normalizedRoleValue === 'serwisant') {
    return 'serviceAdmin';
  }
  if (normalizedRoleValue === 'admin') {
    return 'admin';
  }
  return null;
};

const normalizeProfileStatus = (status: unknown): UserProfile['status'] | null => {
  if (
    status === 'pending' ||
    status === 'pending_verification' ||
    status === 'active' ||
    status === 'suspended' ||
    status === 'rejected'
  ) {
    return status;
  }
  if (status === 'approved') {
    return 'active';
  }
  return null;
};

const ensureActiveUserProfileCallable = httpsCallable<
  undefined,
  { success: boolean; profileState: 'created' | 'patched' | 'already-exists' }
>(functions, 'ensureActiveUserProfile');

const ensureCanonicalUserProfile = async (user: User): Promise<boolean> => {
  try {
    await user.getIdToken(true);
    const result = await ensureActiveUserProfileCallable();
    return result.data?.success === true;
  } catch (error: any) {
    const code = String(error?.code || '');
    if (code.includes('failed-precondition')) {
      console.warn(
        `[AuthContext] Missing profile cannot be auto-bootstrapped for UID ${user.uid}:`,
        error?.message || error
      );
      return false;
    }
    throw error;
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [claims, setClaims] = useState<Claims | null>(null);
  const [authStateLoading, setAuthStateLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [claimsLoading, setClaimsLoading] = useState(true);
  const [claimsInitialized, setClaimsInitialized] = useState(false);
  const [adminClaimRefreshInFlight, setAdminClaimRefreshInFlight] = useState(false);
  const bootstrapAttemptedRef = useRef<Set<string>>(new Set());
  const adminClaimRefreshAttemptedRef = useRef<Set<string>>(new Set());
  const emailVerificationStatusSyncRef = useRef<Set<string>>(new Set());

  const loadClaims = useCallback(
    async (user: User, force = false, silent = false): Promise<void> => {
      if (!silent) {
        setClaimsLoading(true);
      }
      try {
        const idTokenResult = await user.getIdTokenResult(force);
        setClaims(idTokenResult.claims as Claims);
        console.log('[AuthContext] Claims loaded:', {
          uid: user.uid,
          force,
          isAdmin: idTokenResult.claims?.isAdmin === true,
          isServiceAdmin: idTokenResult.claims?.isServiceAdmin === true,
        });
      } catch (error) {
        console.error('[AuthContext] Error fetching ID token claims:', error);
        setClaims(null);
      } finally {
        if (!silent) {
          setClaimsLoading(false);
        }
        setClaimsInitialized(true);
      }
    },
    []
  );

  useEffect(() => {
    let unsubscribeFirestore: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      console.log('Auth state changed, user:', user?.uid || 'none');

      // Clean up previous Firestore listener if it exists
      if (unsubscribeFirestore) {
        console.log('Cleaning up previous Firestore listener.');
        unsubscribeFirestore();
        unsubscribeFirestore = null;
      }

      if (!user) {
        setUserProfile(null);
        setClaims(null);
        setProfileLoading(false);
        setClaimsLoading(false);
        setClaimsInitialized(true);
        adminClaimRefreshAttemptedRef.current.clear();
        emailVerificationStatusSyncRef.current.clear();
        setAdminClaimRefreshInFlight(false);
        setAuthStateLoading(false);
        return;
      }

      setProfileLoading(true);
      setClaimsInitialized(false);
      setClaimsLoading(true);
      adminClaimRefreshAttemptedRef.current.delete(user.uid);

      const userDocRef = doc(firestore, 'users', user.uid);
      unsubscribeFirestore = onSnapshot(
        userDocRef,
        (docSnap) => {
          if (docSnap.exists()) {
            const profileData = docSnap.data() as UserProfile; // Cast to include status
            const normalizedRole = normalizeProfileRole(profileData.role);
            if (!normalizedRole) {
              console.error(
                `[AuthContext] User profile for UID ${user.uid} has missing/invalid role. Refusing implicit role fallback.`,
                profileData
              );
              setUserProfile(null);
              setProfileLoading(false);
              return;
            }
            const fallbackStatus =
              normalizedRole === 'serviceAdmin'
                ? 'active'
                : user.emailVerified === true
                  ? 'active'
                  : 'pending_verification';
            const normalizedStatus = normalizeProfileStatus(profileData.status) || fallbackStatus;
            setUserProfile({
              ...profileData,
              role: normalizedRole,
              status: normalizedStatus,
            });
            console.log('User profile loaded:', profileData);
            setProfileLoading(false);
          } else {
            const alreadyAttempted = bootstrapAttemptedRef.current.has(user.uid);
            if (!alreadyAttempted) {
              bootstrapAttemptedRef.current.add(user.uid);
              ensureCanonicalUserProfile(user)
                .then((createdOrFound) => {
                  if (!createdOrFound) {
                    console.warn(
                      `[AuthContext] User profile missing for UID ${user.uid} and backend bootstrap was not applied.`
                    );
                    // Missing users/{uid} cannot be a steady-state session.
                    // End auth session to avoid durable "Auth OK / profile missing" mode.
                    auth
                      .signOut()
                      .catch((signOutError) => {
                        console.error(
                          `[AuthContext] Failed to sign out after unresolved missing profile for UID ${user.uid}:`,
                          signOutError
                        );
                      })
                      .finally(() => {
                        setUserProfile(null);
                        setProfileLoading(false);
                      });
                  }
                })
                .catch((bootstrapError) => {
                  console.error(
                    `[AuthContext] Failed to bootstrap missing user profile for UID ${user.uid}:`,
                    bootstrapError
                  );
                  setUserProfile(null);
                  setProfileLoading(false);
                });
              return;
            }

            console.warn(`User document not found for UID: ${user.uid}`);
            setUserProfile(null);
            setProfileLoading(false);
          }
        },
        (error) => {
          console.error('Error fetching user profile:', error);
          setUserProfile(null);
          setProfileLoading(false);
        }
      );

      setAuthStateLoading(false);
    });

    return () => {
      console.log('Unsubscribing from Auth state listener');
      unsubscribeAuth();
      if (unsubscribeFirestore) {
        console.log('Unsubscribing from Firestore profile listener on component unmount.');
        unsubscribeFirestore();
      }
    };
  }, [loadClaims]);

  useEffect(() => {
    if (!currentUser || !userProfile) return;
    if (userProfile.role !== 'client') return;
    if (profileLoading) return;

    const expectedStatus = currentUser.emailVerified === true ? 'active' : 'pending_verification';
    if (userProfile.status !== 'active' && userProfile.status !== 'pending_verification') return;
    if (userProfile.status === expectedStatus) return;

    const syncKey = `${currentUser.uid}:${expectedStatus}`;
    if (emailVerificationStatusSyncRef.current.has(syncKey)) return;
    emailVerificationStatusSyncRef.current.add(syncKey);

    syncEmailVerificationStatus()
      .then((result) => {
        if (result.success !== true) {
          emailVerificationStatusSyncRef.current.delete(syncKey);
        }
      })
      .catch((error: any) => {
        const code = String(error?.code || '');
        if (code.includes('failed-precondition')) {
          return;
        }
        emailVerificationStatusSyncRef.current.delete(syncKey);
        console.error('[AuthContext] Failed to sync email verification profile status:', error);
      });
  }, [currentUser, userProfile, profileLoading]);

  useEffect(() => {
    const unsubscribeIdToken = onIdTokenChanged(auth, async (user) => {
      if (!user) {
        setClaims(null);
        setClaimsLoading(false);
        setClaimsInitialized(true);
        return;
      }
      const silent = claimsInitialized;
      await loadClaims(user, false, silent);
    });

    return () => {
      unsubscribeIdToken();
    };
  }, [claimsInitialized, loadClaims]);

  useEffect(() => {
    if (!currentUser) return;
    if (userProfile?.role !== 'admin') return;
    if (claims?.isAdmin === true) return;
    if (claimsLoading || adminClaimRefreshInFlight) return;
    if (adminClaimRefreshAttemptedRef.current.has(currentUser.uid)) return;

    adminClaimRefreshAttemptedRef.current.add(currentUser.uid);
    setAdminClaimRefreshInFlight(true);
    loadClaims(currentUser, true).finally(() => {
      setAdminClaimRefreshInFlight(false);
    });
  }, [
    currentUser,
    userProfile?.role,
    claims?.isAdmin,
    claimsLoading,
    adminClaimRefreshInFlight,
    loadClaims,
  ]);

  const refreshClaims = useCallback(
    async (force = true): Promise<void> => {
      if (!auth.currentUser) return;
      await loadClaims(auth.currentUser, force);
    },
    [loadClaims]
  );

  useEffect(() => {
    if (!currentUser) return;
    if (claims?.isAdmin !== true) return;

    const refreshIfPossible = () => {
      if (!auth.currentUser) return;
      void loadClaims(auth.currentUser, true, true);
    };

    const intervalId = window.setInterval(refreshIfPossible, 60000);
    const handleFocus = () => {
      refreshIfPossible();
    };
    const handleVisibility = () => {
      if (!document.hidden) {
        refreshIfPossible();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [currentUser, claims?.isAdmin, loadClaims]);

  const adminAccessResolving = Boolean(
    currentUser &&
      userProfile?.role === 'admin' &&
      claims?.isAdmin !== true &&
      (claimsLoading || adminClaimRefreshInFlight)
  );

  const initialClaimsPending = Boolean(currentUser && !claimsInitialized);
  // Global app loading must not depend on privileged admin claim resolving.
  // Otherwise revoke flow could temporarily freeze standard user pages.
  const loading = authStateLoading || profileLoading || initialClaimsPending;

  const value = useMemo(
    () => ({
      currentUser,
      userProfile,
      claims,
      claimsLoading,
      adminAccessResolving,
      refreshClaims,
      loading,
    }),
    [currentUser, userProfile, claims, claimsLoading, adminAccessResolving, refreshClaims, loading]
  );

  return <AuthContext.Provider value={value}>{!loading ? children : null}</AuthContext.Provider>;
};
